import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Equipment Details Card
 *
 * FT-531: Serial Number / Model inputs used to call onSave on EVERY
 * keystroke (no debounce, no save button). That made per-keystroke audit
 * logging a non-starter — logging each character typed would flood the
 * audit trail with noise. These two fields now debounce 1000ms before
 * saving, mirroring EquipmentNotesCard's existing auto-save pattern exactly
 * (local buffer state synced from props + a cleared/reset setTimeout).
 *
 * onFieldSave(auditField, value, extraFields) replaces the old onSave(obj)
 * prop so the parent (EquipmentDetailView) knows exactly WHICH single field
 * changed and can diff just that field for the audit entry — see FT-531.
 * extraFields carries the legacy mirror key (serialNumber / specifications)
 * that must also be written for backward-compat reads elsewhere in the app,
 * without those mirror keys being treated as a second audited field.
 */
const EquipmentDetailsCard = ({ equipment, allEquipment = [], onFieldSave, lastMaintenanceRecord }) => {
  const [duplicateWarning, setDuplicateWarning] = useState(false);

  const [localSerial, setLocalSerial] = useState(equipment.serialNo || equipment.serialNumber || '');
  const [localModel, setLocalModel] = useState(equipment.modelType || equipment.specifications || '');
  const serialTimeoutRef = useRef(null);
  const modelTimeoutRef = useRef(null);

  // FT-531 fix-first (Bug 2 — lost edits on unmount): always call the LATEST
  // onFieldSave via a ref, so a flush (below) never fires with a prop
  // reference frozen at some earlier render.
  const onFieldSaveRef = useRef(onFieldSave);
  onFieldSaveRef.current = onFieldSave;

  // Holds the value that WOULD be saved when the pending timer fires, so a
  // flush (unmount or blur, before the timer naturally fires) can save it
  // immediately instead of the edit being silently dropped. null = nothing
  // pending for that field.
  const pendingSerialRef = useRef(null);
  const pendingModelRef = useRef(null);

  // Keep local buffers in sync if the underlying equipment prop changes
  // from elsewhere (e.g. sync pull, or switching equipment items).
  useEffect(() => {
    setLocalSerial(equipment.serialNo || equipment.serialNumber || '');
  }, [equipment.serialNo, equipment.serialNumber]);

  useEffect(() => {
    setLocalModel(equipment.modelType || equipment.specifications || '');
  }, [equipment.modelType, equipment.specifications]);

  // FT-531 fix-first (Bug 2): FLUSH — save a pending edit RIGHT NOW instead
  // of discarding it.
  const flushSerial = () => {
    if (serialTimeoutRef.current) {
      clearTimeout(serialTimeoutRef.current);
      serialTimeoutRef.current = null;
    }
    if (pendingSerialRef.current) {
      const { value } = pendingSerialRef.current;
      pendingSerialRef.current = null;
      onFieldSaveRef.current('serialNo', value, { serialNo: value, serialNumber: value });
    }
  };

  const flushModel = () => {
    if (modelTimeoutRef.current) {
      clearTimeout(modelTimeoutRef.current);
      modelTimeoutRef.current = null;
    }
    if (pendingModelRef.current) {
      const { value } = pendingModelRef.current;
      pendingModelRef.current = null;
      onFieldSaveRef.current('modelType', value, { modelType: value, specifications: value });
    }
  };

  // FT-531 fix-first (Bug 2): on unmount, FLUSH pending debounced saves
  // instead of merely clearing the timer and dropping the edit. Before this
  // fix, typing a Serial Number or Model/Type then tapping Back, or
  // switching to the History/Documents tab (this card unmounts whenever the
  // Details tab isn't selected), within 1000ms silently lost the edit — no
  // save, no audit entry, nothing. This is a REGRESSION specific to FT-531:
  // before debouncing was added, these fields saved on every keystroke, so
  // this loss window didn't exist.
  useEffect(() => {
    return () => {
      flushSerial();
      flushModel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDuplicate = (value) => {
    if (!value || value.trim() === '') return false;
    return allEquipment.some(
      item => item.id !== equipment.id &&
      (item.serialNo === value || item.serialNumber === value)
    );
  };

  const handleSerialChange = (e) => {
    const value = e.target.value;
    setLocalSerial(value);
    // Duplicate warning stays live per-keystroke — it's just a UI hint, not a save.
    setDuplicateWarning(checkDuplicate(value));

    pendingSerialRef.current = { value };
    if (serialTimeoutRef.current) clearTimeout(serialTimeoutRef.current);
    serialTimeoutRef.current = setTimeout(() => {
      serialTimeoutRef.current = null;
      pendingSerialRef.current = null;
      onFieldSaveRef.current('serialNo', value, { serialNo: value, serialNumber: value });
    }, 1000); // 1 second debounce — matches EquipmentNotesCard
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    setLocalModel(value);

    pendingModelRef.current = { value };
    if (modelTimeoutRef.current) clearTimeout(modelTimeoutRef.current);
    modelTimeoutRef.current = setTimeout(() => {
      modelTimeoutRef.current = null;
      pendingModelRef.current = null;
      onFieldSaveRef.current('modelType', value, { modelType: value, specifications: value });
    }, 1000);
  };

  return (
    <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
      <div className="bg-slate-700 px-5 py-3">
        <h3 className="text-base font-semibold text-white">Equipment Details</h3>
      </div>
      <div className="px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Serial Number */}
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-gray-700 font-medium mb-1">
              Serial Number
            </label>
            <input
              type="text"
              value={localSerial}
              onChange={handleSerialChange}
              onBlur={flushSerial}
              placeholder="Enter serial number..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
            />
            {duplicateWarning && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mt-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Another equipment item has this serial number</span>
              </div>
            )}
          </div>

          {/* Model/Type */}
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-gray-700 font-medium mb-1">
              Model/Type
            </label>
            <input
              type="text"
              value={localModel}
              onChange={handleModelChange}
              onBlur={flushModel}
              placeholder="Enter model or type..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
            />
          </div>

          {/* Last Service Date - READ ONLY (populated from Maintenance) */}
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-gray-700 font-medium mb-1">
              Last Service Date
            </label>
            <div className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700">
              {lastMaintenanceRecord?.date ? (
                new Date(lastMaintenanceRecord.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short', 
                  year: 'numeric'
                })
              ) : equipment.lastServiceAt ? (
                new Date(equipment.lastServiceAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short', 
                  year: 'numeric'
                })
              ) : (
                'No service recorded'
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Updated automatically when maintenance is completed
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EquipmentDetailsCard;
